import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(16px); }
  to { opacity: 1; transform: translateX(0); }
`;

const Shell = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: #ffffff;
  font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
  color: #1e293b;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FormPane = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 2.5rem 3.5rem 1.75rem;
  background: #ffffff;
  animation: ${fadeIn} 0.45s ease;

  @media (max-width: 900px) {
    padding: 1.75rem 1.5rem 1.5rem;
    min-height: 100vh;
  }
`;

const FormInner = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 2.25rem;
`;

const Title = styled.h1`
  margin: 0 0 1.75rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.02em;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Label = styled.label`
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0.8rem 0.95rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #1e293b;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: #0B63C5;
    box-shadow: 0 0 0 3px rgba(91, 91, 214, 0.15);
  }
`;

const PasswordWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordInput = styled(Input)`
  padding-right: 2.75rem;
`;

const EyeButton = styled.button`
  position: absolute;
  right: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.25rem;
  display: grid;
  place-items: center;

  &:hover {
    color: #0B63C5;
  }
`;

const OptionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.15rem;
`;

const Remember = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.8125rem;
  color: #64748b;
  cursor: pointer;
  user-select: none;

  input {
    width: 0.95rem;
    height: 0.95rem;
    accent-color: #0B63C5;
    cursor: pointer;
  }
`;

const ForgotLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: #0B63C5;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const SubmitButton = styled.button`
  margin-top: 0.35rem;
  width: 100%;
  border: none;
  border-radius: 10px;
  padding: 0.85rem 1rem;
  background: #0B63C5;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;

  &:hover:not(:disabled) {
    background: #0A55A8;
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(91, 91, 214, 0.28);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  color: #dc2626;
  font-size: 0.8125rem;
  text-align: center;
`;

const Footer = styled.p`
  margin: 2rem 0 0;
  text-align: center;
  font-size: 0.75rem;
  color: #94a3b8;
`;

const ArtPane = styled.div`
  background: #eef0fb;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem;
  position: relative;
  overflow: hidden;
  animation: ${slideIn} 0.5s ease;

  @media (max-width: 900px) {
    display: none;
  }
`;

const HospitalBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 2rem;
`;

const CrossCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #ffffff;
  display: grid;
  place-items: center;
  box-shadow: 0 10px 24px rgba(11, 99, 197, 0.18);
`;

const HospitalWord = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: #0B63C5;
`;

const IllustrationWrap = styled.div`
  width: min(340px, 80%);
`;

const CrossIcon = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M10 3h4v7h7v4h-7v7h-4v-7H3v-4h7V3z" fill="#0B63C5" />
  </svg>
);

const EyeOpenIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 3l18 18M10.6 10.6A3 3 0 0012 15a3 3 0 002.4-4.8M9.9 5.2A10.5 10.5 0 0112 5c6.5 0 10 7 10 7a18.4 18.4 0 01-4.2 4.8M6.1 6.1C3.9 7.7 2 12 2 12s3.5 7 10 7c1.4 0 2.7-.3 3.9-.8"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ReceptionArt = () => (
  <svg viewBox="0 0 360 300" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Reception desk illustration">
    <ellipse cx="180" cy="272" rx="118" ry="16" fill="#d8daf5" />
    <rect x="198" y="118" width="52" height="70" rx="18" fill="#4c1d95" opacity="0.35" />
    <circle cx="210" cy="92" r="30" fill="#1f2937" />
    <path d="M176 128c10-22 58-22 68 0v42c-12 22-56 22-68 0v-42z" fill="#0B63C5" />
    <path d="M184 156h52v48c-10 14-42 14-52 0v-48z" fill="#fff" />
    <rect x="58" y="188" width="244" height="58" rx="8" fill="#e8eaf8" />
    <rect x="58" y="188" width="244" height="10" fill="#d4d8f3" />
    <rect x="78" y="118" width="86" height="62" rx="6" fill="#1e1b4b" />
    <rect x="86" y="126" width="70" height="42" rx="3" fill="#60A5FA" />
    <rect x="110" y="180" width="22" height="10" fill="#312e81" />
    <rect x="178" y="208" width="70" height="10" rx="3" fill="#c7d2fe" />
    <ellipse cx="268" cy="214" rx="8" ry="5" fill="#a5b4fc" />
    <rect x="286" y="198" width="18" height="22" rx="4" fill="#0B63C5" />
  </svg>
);

const Subtitle = styled.p`
  margin: -1.25rem 0 1.75rem;
  color: #64748b;
  font-size: 0.9rem;
`;

const ArtCaption = styled.p`
  margin: 1.5rem 0 0;
  max-width: 340px;
  text-align: center;
  color: #334155;
  font-size: 0.95rem;
  line-height: 1.55;
`;

const SplitLoginLayout = ({
  title,
  subtitle,
  children,
  artSrc,
  artAlt = '',
  artCaption,
  badgeLabel = 'HOSPITAL',
  brandExtra,
  footerYear = new Date().getFullYear(),
}) => (
  <Shell>
    <FormPane>
      <FormInner>
        <Brand>
          <img src="/logo-wordmark.jpg" alt="Anuvedana" style={{ width: 96, height: 96, borderRadius: 16 }} />
          {brandExtra}
        </Brand>
        <Title>{title}</Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
        {children}
      </FormInner>
      <Footer>© {footerYear} Anuvedana. All rights reserved.</Footer>
    </FormPane>

    <ArtPane>
      <HospitalBadge>
        <CrossCircle>
          <CrossIcon />
        </CrossCircle>
        <HospitalWord>{badgeLabel}</HospitalWord>
      </HospitalBadge>
      <IllustrationWrap style={artSrc ? { width: 'min(420px, 85%)' } : undefined}>
        {artSrc
          ? <img src={artSrc} alt={artAlt} style={{ width: '100%', height: 'auto', borderRadius: 24, mixBlendMode: 'multiply' }} />
          : <ReceptionArt />}
      </IllustrationWrap>
      {artCaption && <ArtCaption>{artCaption}</ArtCaption>}
    </ArtPane>
  </Shell>
);

export {
  SplitLoginLayout,
  Form,
  Field,
  Label,
  Input,
  PasswordWrap,
  PasswordInput,
  EyeButton,
  EyeOpenIcon,
  EyeOffIcon,
  OptionsRow,
  Remember,
  ForgotLink,
  SubmitButton,
  ErrorText,
};

export default SplitLoginLayout;
